import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ClassRecord.css';
import { findLanguageByCode } from '../constants/languages';
import api from '../utils/api';

// 개별 발언 레코드 인터페이스
interface TranscriptItem {
    originalText: string;
    translations: Record<string, string>;
    timestamp: string;
    hasTranslations: boolean;
    isProfessor: boolean; // 추가: 교수/학생 구분
}

// 통합 데이터 인터페이스
interface ConsolidatedData {
    professorTranscripts: Omit<TranscriptItem, 'isProfessor'>[];
    studentTranscripts: Omit<TranscriptItem, 'isProfessor'>[];
    metadata: {
        totalItems: number;
        startTime: string;
        endTime: string;
        languages: string[];
    };
}

// 원본 API 응답 인터페이스
interface TranslationRecord {
    id: string;
    classId: string;
    originalText: string;
    translations: {
        consolidated: string; // JSON 문자열
        [key: string]: string;
    };
    timestamp: string;
    isProfessor: boolean;
}
interface StudentCounselProfile {
    id?: string;
    classId?: string;
    name: string;
    nation: string;
    email: string;
    createdAt?: string;
    updatedAt?: string;
}

// ClassRecord component
const ClassRecord: React.FC = () => {
    const { classId } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    // State variables
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [classData, setClassData] = useState<any>(null);
    const [transcripts, setTranscripts] = useState<TranscriptItem[]>([]);
    const [hasEnglishTranslation, setHasEnglishTranslation] = useState<boolean>(false);

    // 상담자 프로필 관련 상태
    const [isConsultationLayout, setIsConsultationLayout] = useState<boolean>(false);
    const [counselProfile, setCounselProfile] = useState<StudentCounselProfile | null>(null);
    const [loadingProfile, setLoadingProfile] = useState<boolean>(false);

    // Fetch class data and translations
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);

                // Fetch class data
                const classResponse = await api.get(`/classes/${classId}`);
                setClassData(classResponse.data.result);

                // 강의 레이아웃이 상담인지 확인
                const isConsultation = classResponse.data.result?.layout === 'CONSULTATION';
                setIsConsultationLayout(isConsultation);

                // 상담 레이아웃인 경우 상담자 프로필 가져오기
                if (isConsultation) {
                    fetchCounselProfile();
                }

                // Fetch translations
                const translationsResponse = await api.get(`/classes/${classId}/translations`);

                // API 응답 구조에 맞게 데이터 추출
                let allTranscripts: TranscriptItem[] = [];

                if (
                    translationsResponse.data.result &&
                    Array.isArray(translationsResponse.data.result)
                ) {
                    const rawData = translationsResponse.data.result;

                    // 각 레코드에서 consolidated 데이터 파싱
                    rawData.forEach((record: TranslationRecord) => {
                        try {
                            if (record.translations?.consolidated) {
                                // JSON 문자열을 객체로 파싱
                                const consolidatedData: ConsolidatedData = JSON.parse(
                                    record.translations.consolidated,
                                );

                                // 교수 발언 처리
                                if (
                                    consolidatedData.professorTranscripts &&
                                    Array.isArray(consolidatedData.professorTranscripts)
                                ) {
                                    const professorItems =
                                        consolidatedData.professorTranscripts.map(item => ({
                                            ...item,
                                            isProfessor: true,
                                        }));
                                    allTranscripts = [...allTranscripts, ...professorItems];
                                }

                                // 학생 발언 처리
                                if (
                                    consolidatedData.studentTranscripts &&
                                    Array.isArray(consolidatedData.studentTranscripts)
                                ) {
                                    const studentItems = consolidatedData.studentTranscripts.map(
                                        item => ({
                                            ...item,
                                            isProfessor: false,
                                        }),
                                    );
                                    allTranscripts = [...allTranscripts, ...studentItems];
                                }

                                // 영어 번역 여부 확인 (metadata)
                                if (consolidatedData.metadata?.languages?.includes('en-US')) {
                                    setHasEnglishTranslation(true);
                                }
                            }
                        } catch (parseError) {
                            console.error('consolidated 데이터 파싱 오류:', parseError);
                        }
                    });
                }

                // 시간순으로 정렬
                allTranscripts.sort(
                    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
                );

                setTranscripts(allTranscripts);
                setIsLoading(false);
            } catch (err: any) {
                console.error('강의 기록 로딩 오류:', err);
                setError(
                    err.response?.data?.message || '강의 기록을 불러오는 중 오류가 발생했습니다.',
                );
                setIsLoading(false);
            }
        };

        fetchData();
    }, [classId]);

    // 상담자 프로필 가져오기
    const fetchCounselProfile = async () => {
        try {
            setLoadingProfile(true);
            const response = await api.get(`/classes/${classId}/counsel`);
            if (response.data.result) {
                // 결과가 배열인 경우 첫 번째 요소 사용
                if (Array.isArray(response.data.result) && response.data.result.length > 0) {
                    setCounselProfile(response.data.result[0]);
                } else {
                    setCounselProfile(response.data.result);
                }
            }
            setLoadingProfile(false);
        } catch (err: any) {
            console.error('상담자 프로필 로딩 오류:', err);
            // 프로필이 없는 경우는 에러가 아닌 것으로 처리
            setCounselProfile(null);
            setLoadingProfile(false);
        }
    };

    // Format timestamp to Korean time
    const formatTimestamp = (timestamp: string) => {
        return new Intl.DateTimeFormat('ko-KR', {
            timeZone: 'Asia/Seoul',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        }).format(new Date(timestamp));
    };

    // Handle back button click
    const handleBack = () => {
        navigate('/dashboard');
    };

    // Save translations to a text file
    const handleSaveToTxt = () => {
        try {
            // Create text content
            let content = `강의 기록: ${classData?.title || '(제목 없음)'}\n`;
            content += `날짜: ${formatTimestamp(classData?.createdAt)}\n\n`;

            // Add translations
            transcripts.forEach(item => {
                content += `[${item.isProfessor ? '교수' : '학생'}] ${formatTimestamp(
                    item.timestamp,
                )}\n`;
                content += `${item.originalText}\n`;

                // 모든 번역 추가
                if (item.translations) {
                    // translations가 문자열인 경우
                    if (typeof item.translations === 'string') {
                        content += `번역: ${item.translations}\n`;
                    }
                    // translations가 객체인 경우
                    else if (typeof item.translations === 'object') {
                        // consolidated 키가 있는 경우 건너뛰기
                        const translationEntries = Object.entries(item.translations).filter(
                            ([key]) => key !== 'consolidated',
                        );

                        if (translationEntries.length > 0) {
                            const [, translationText] = translationEntries[0];
                            content += `번역: ${translationText}\n`;
                        }
                    }
                }

                content += '\n';
            });

            // Create blob and download link
            const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `강의기록_${classId}_${new Date().toISOString().slice(0, 10)}.txt`;
            document.body.appendChild(a);
            a.click();

            // Clean up
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('텍스트 파일 저장 중 오류 발생:', err);
            alert('텍스트 파일로 저장하는 중 오류가 발생했습니다.');
        }
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>강의 기록을 불러오는 중입니다...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <h3>오류가 발생했습니다</h3>
                <p>{error}</p>
                <button onClick={handleBack}>돌아가기</button>
            </div>
        );
    }

    return (
        <div className="class-record-container">
            <div className="record-header">
                <button className="back-button" onClick={handleBack}>
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>{' '}
                    돌아가기
                </button>
                <h2>{classData?.title || '강의 기록'}</h2>
                <button className="save-button" onClick={handleSaveToTxt}>
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                    </svg>{' '}
                    TXT로 저장
                </button>
            </div>

            <div className="record-meta">
                <p>강의 날짜: {formatTimestamp(classData?.createdAt)}</p>
                <p>Code Number: {classId}</p>
                <p>
                    레이아웃:{' '}
                    {classData?.layout === 'CONSULTATION'
                        ? '상담'
                        : classData?.layout === 'PRESENTATION'
                        ? '발표'
                        : '일반 강의'}
                </p>
            </div>

            {/* 상담자 프로필 표시 (상담 레이아웃인 경우만) */}
            {isConsultationLayout && (
                <div className="counsel-profile-container">
                    <h3 className="counsel-profile-title">상담학생 프로필</h3>
                    {loadingProfile ? (
                        <div className="counsel-profile-loading">
                            <div className="loading-spinner-small"></div>
                            <p>상담자 프로필을 불러오는 중...</p>
                        </div>
                    ) : counselProfile ? (
                        <div className="counsel-profile-info">
                            <div className="counsel-profile-field">
                                <span className="field-label">이름:</span>
                                <span className="field-value">{counselProfile.name}</span>
                            </div>
                            <div className="counsel-profile-field">
                                <span className="field-label">국가:</span>
                                <span className="field-value">{counselProfile.nation}</span>
                            </div>
                            {counselProfile.email && (
                                <div className="counsel-profile-field">
                                    <span className="field-label">이메일:</span>
                                    <span className="field-value">{counselProfile.email}</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="counsel-profile-empty">
                            <p>등록된 상담학생 프로필이 없습니다.</p>
                        </div>
                    )}
                </div>
            )}

            <div className="translations-container">
                {transcripts.length === 0 ? (
                    <div className="no-records">
                        <p>강의 기록이 없습니다.</p>
                    </div>
                ) : (
                    transcripts.map((item, index) => (
                        <div
                            key={index}
                            className={`translation-item ${
                                item.isProfessor ? 'professor' : 'student'
                            }`}
                        >
                            <div className="translation-header">
                                <span className="speaker-type">
                                    {item.isProfessor ? '교수' : '학생'}
                                </span>
                                <span className="timestamp">{formatTimestamp(item.timestamp)}</span>
                            </div>

                            <div className="translation-content">
                                <p className="original-text">{item.originalText}</p>

                                {/* 번역 결과가 문자열인 경우를 처리 */}
                                {item.translations && typeof item.translations === 'string' ? (
                                    <p className="translated-text">
                                        <span className="language-label">번역: </span>
                                        {item.translations}
                                    </p>
                                ) : (
                                    // 번역 결과가 객체인 경우 (기존 방식)
                                    item.translations &&
                                    Object.keys(item.translations).map(langCode => {
                                        if (
                                            langCode !== 'consolidated' &&
                                            item.translations[langCode]
                                        ) {
                                            const language = findLanguageByCode(langCode);
                                            const languageName = language
                                                ? `${language.koreanName} (${language.nativeName})`
                                                : langCode;

                                            return (
                                                <p key={langCode} className="translated-text">
                                                    <span className="language-label">
                                                        {languageName}:{' '}
                                                    </span>
                                                    {item.translations[langCode]}
                                                </p>
                                            );
                                        }
                                        return null;
                                    })
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ClassRecord;
